
<template>
  <div class="recipe-form">
    <ez-image-upload
      :formKey="formKey"
      :previewUrl="draft.image || defaultImage"
      :isDefaultImage="!draft.image"
      columnMode
      name="image"
      @removeImage="onRemoveImage"
      @change="onUpdateImage"
    >
      <template #addImage>
        <span>Upload Item Image</span>
      </template>
      <template #removeImage>
        <font-awesome-icon icon="times" />
        <span>Remove Image</span>
      </template>
    </ez-image-upload>
    <ez-input
      :formKey="formKey"
      name="name"
      label="Item name*"
      :value="draft.name"
      @onChange="onNameChange"
      placeholder="Enter Item Name"
    ></ez-input>
    <ez-dropdown
      :formKey="formKey"
      name="type"
      label="Type"
      :selected="draft.type"
      @change="onTypeChange"
      :data="recipeTypes"
      isFullWidth
      placeholder="Please select item type"
    ></ez-dropdown>
    <v-select-search-unit
      ref="chooseUnit"
      :data="units"
      label="Unit*"
      placeholder="Choose Unit"
      name="unitId"
      search-property="label"
      has-actions
      @onEdit="editUnit"
      @onRemove="deleteUnit"
      @onAddNew="openUnitModal"
      @onSelect="selectUnit"
      :selected-id="draft.unitId"
    />
    <ez-mask-input
      v-if="isRecipe"
      ref="minQuantityDecimal"
      label="Quantity*"
      name="quantity"
      :form-key="formKey"
      :precision="2"
      class="mask-input mt-12"
      :value="draft.quantity || 0"
      type="input"
      :price-prefix="false"
      @input="onQuantityChange"
    />
    <ez-mask-input
      v-if="isMenuItem"
      :form-key="formKey"
      name="salesPrice"
      label="Price*"
      class="mask-input mt-12"
      :value="draft.salesPrice || 0"
      type="input"
      @input="onPriceChange"
    />
    <div
      v-if="hasManageInventoryPermission && isInventoryEnabled"
      class="toggle-container mt-12">

      <div class="toggle-label">
        <span>Inventory Tracked</span>
      </div>
      <ez-on-off
        :formKey="formKey"
        :checked="draft.inventoryTracked || false"
        @change="onInventoryTrackedChange"
        name="inventoryTracked"
      />
    </div>
    <add-unit-modal
      ref="addUnitModal"
      :unit="unitToUpdate"
      @onClose="onUnitModalClose"
      @success="getUnits"
    />
    <remove-unit-modal
      ref="removeUnit"
      @onClose="onRemoveClose"
      @success="() => $emit('successDeleteUnit')"
      :unit="unitToDelete"
    />
  </div>
</template>

<script>
/**
 * SelectVenue
 * @version 1.0.0
 * @since 2.3.0
 */

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import EzDropdown from '@/components/ui/Dropdown';
import EzImageUpload from '@/components/ui/ImageUpload';
import EzInput from '@/components/ui/Input';
import EzOnOff from '@/components/ui/OnOff/EzOnOff';
import { getCurrency } from '@/util/utils';
import { wizardListenerMixin } from '@/mixins/wizard';
import EzMaskInput from '@/components/ui/MaskInput/EzMaskInput';
import AddUnitModal from '@/components/v3/patterns/VUnitsSettings/AddUnitModal';
import RemoveUnitModal from '@/components/v3/patterns/VUnitsSettings/RemoveUnitModal';
import VSelectSearchUnit from '@/components/v3/patterns/VSelectSearchUnit';

export default {
  mixins: [wizardListenerMixin],
  components: {
    EzDropdown,
    EzImageUpload,
    EzInput,
    EzOnOff,
    EzMaskInput,
    AddUnitModal,
    RemoveUnitModal,
    VSelectSearchUnit,
  },
  data() {
    return {
      recipeTypes: [],
      formKey: 'recipeForm',
      units: [],
      selectedUnit: {},
      unitToUpdate: {},
      unitToDelete: {},
    };
  },
  computed: {
    ...mapState('entities/users', ['context', 'contextId', 'loggedUser']),
    ...mapState('recipes', ['draft']),
    ...mapGetters('defaultImages', ['getDefaultImage']),
    defaultImage() {
      return this.getDefaultImage('category');
    },
    currencySymbol() {
      const { symbol } = getCurrency() || {};
      return symbol;
    },
    isRecipe() {
      return this.draft.type === 'recipe';
    },
    isMenuItem() {
      return this.draft.type === 'menu_item';
    },
    isInventoryEnabled() {
      return this.context?.features?.inventory || false;
    },
    hasManageInventoryPermission() {
      return this.$permission.has('manageInventory');
    },
  },
  methods: {
    ...mapMutations('recipes', ['CLEAR_DRAFT', 'UPDATE_DRAFT_RECIPE']),
    ...mapActions('entities/products', ['venueFetchUnits']),
    ...mapActions('recipes', ['venueFetchRecipeTypes']),
    async getRecipeTypes() {
        const { data } = await this.venueFetchRecipeTypes();
        this.recipeTypes = data.data || [];

        if (this.draft.type && !this.draft.typeObject) {
          const typeObject = this.recipeTypes.find(type => type.id === this.draft.type);
          if (typeObject) {
            this.UPDATE_DRAFT_RECIPE({ typeObject });
          }
        }
    },

    async getUnits() {
      const {
        data: { data },
      } = await this.venueFetchUnits();
      this.units = data;
    },
    validateFirstStep() {
      if (!this.draft.name || this.draft.unitId === null || this.draft.unitId === undefined) {
        this.disableNextStep();
        return;
      }
      if (this.isRecipe && !this.draft.quantity) {
        this.disableNextStep();
        return;
      }
      if (this.isMenuItem && !this.draft.salesPrice === null) {
        this.disableNextStep();
        return;
      }
      this.enableNextStep();
    },
    selectUnit(unit) {
      this.selectedUnit = unit;
      this.UPDATE_DRAFT_RECIPE({ unitId: unit.id, unit });
      this.validateFirstStep();
    },
    deleteUnit(unit) {
      this.unitToDelete = unit;
      this.$refs.removeUnit.open();
    },
    editUnit(unit) {
      if (!unit.owned) return;
      this.unitToUpdate = unit;
      this.openUnitModal();
    },
    openUnitModal() {
      this.$refs.addUnitModal.open();
    },
    onUnitModalClose() {
      this.unitToUpdate = {};
    },
    onRemoveClose() {
      if (this.selectedUnit?.id === this.unitToDelete?.id) {
        this.$refs.chooseUnit.reset();
        this.preSelectData(this.product);
      }
      this.unitToDelete = {};
    },
    onNameChange(value) {
      this.UPDATE_DRAFT_RECIPE({ name: value });
      this.validateFirstStep();
    },

    onTypeChange(value) {
      this.UPDATE_DRAFT_RECIPE({
        type: value.id,
        typeObject: value,
        items: [],
        salesPrice: value.id === 'menu_item' ? 0 : null,
        quantity: value.id === 'recipe' ? 1 : null,
        unitId: null,
      });
      this.$refs.chooseUnit.reset();
      this.validateFirstStep();
    },
    onInventoryTrackedChange(value) {
      this.UPDATE_DRAFT_RECIPE({ inventoryTracked: value });
      this.validateFirstStep();
    },
    onPriceChange(value) {
      this.UPDATE_DRAFT_RECIPE({ salesPrice: value });
      this.validateFirstStep();
    },
    onQuantityChange(value) {
      this.UPDATE_DRAFT_RECIPE({ quantity: value });
      this.validateFirstStep();
    },
    onUpdateImage(image) {
      if (image) {
        this.UPDATE_DRAFT_RECIPE({image, imgModified: true});
        this.validateFirstStep();
      }
    },
    onRemoveImage() {
      this.UPDATE_DRAFT_RECIPE({ image: null, imgModified: true });
      this.validateFirstStep();
    },
    async onNextStep() {
      this.$emit('stepCompleted');
    },
  },
  async mounted() {
    await this.getRecipeTypes();
    await this.getUnits();
    this.validateFirstStep();
  },
};
</script>

<style lang="scss" scoped>
.recipe-form {
  margin: 2rem;
  .input-group {
    + .input-group {
      margin-top: 0.75rem;
    }
  }
  :deep() .ez-image-upload {
    margin-bottom: 1rem;
  }
}

.toggle-container {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .toggle-label {
    display: flex;
    flex-direction: column;

    span {
      font-size: 14px;
      font-weight: 400;
      margin-bottom: 0.25rem;
    }

    small {
      font-size: 12px;
      color: #6C757D;
    }
  }
}

:deep() .mask-input .mask-input__input {
  font-weight: normal;
}

:deep() .select-search__wrapper {
  font-size: 14px;
}
</style>
